import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import AddIcon from '@mui/icons-material/Add';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import TaskIcon from '@mui/icons-material/Task';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import NoteIcon from '@mui/icons-material/Note';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axiosInstance from './axiosInstance';
import { useSnackbar } from '../providers/SnackbarProvider';
import { useTheme, alpha } from '@mui/material/styles';

import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';


const InteractionTimeline = ({ relatedType, relatedId }) => {
  const { showSnackbar } = useSnackbar();
  const [interactions, setInteractions] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [editInteractionDialogOpen, setEditInteractionDialogOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [dateError, setDateError] = useState(null);
  
  const [newInteraction, setNewInteraction] = useState({
    type: '',
    title: '',
    description: '',
    assignee: null,
    due_date: null,
  });

  useEffect(() => {
    fetchInteractions();
    fetchUsers();
    fetchCurrentUser();
  }, [relatedType, relatedId]);

  const fetchInteractions = async () => {
    try {
      const response = await axiosInstance.get('/interactions/', {
        params: {
          related_type: relatedType,
          related_id: relatedId,
        },
      });
      setInteractions(response.data);
    } catch (error) {
      console.error('Error fetching interactions', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const response = await axiosInstance.get('/me/');
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'CALL':
        return <PhoneIcon />;
      case 'EMAIL':
        return <EmailIcon />;
      case 'TASK':
        return <TaskIcon />;
      case 'MEETING':
        return <MeetingRoomIcon />;
      case 'NOTE':
        return <NoteIcon />;
      default:
        return <NoteIcon />;
    }
  };
  
  const theme = useTheme();

  const interactionColorMap = {
    CALL: theme.palette.interaction.call,
    EMAIL: theme.palette.interaction.email,
    TASK: theme.palette.interaction.task,
    MEETING: theme.palette.interaction.meeting,
    NOTE: theme.palette.interaction.note,
  };

  const getInteractionColor = (type) =>
    interactionColorMap[type] || theme.palette.interaction.note;

  const getRelativeDate = (dateStr) => {
    const date = dayjs(dateStr).startOf('day');
    const today = dayjs().startOf('day');
    const diff = today.diff(date, 'day');
    if (diff === 0) return 'Heute';
    if (diff === 1) return 'Gestern';
    if (diff === 2) return 'Vor 2 Tagen';
    return dayjs(dateStr).format('DD.MM');
  };

  const handleAddInteraction = async () => {
    try {
      const response = await axiosInstance.post('/interactions/', {
        ...newInteraction,
        related_type: relatedType,
        object_id: relatedId,
      });

      setInteractions((prev) => [response.data, ...prev]);
      setShowInteractionForm(false);
      setNewInteraction({
        type: '',
        title: '',
        description: '',
        assignee: null,
        due_date: null,
      });
      showSnackbar("Interaktion erfolgreich hinzugefügt", "success");
    } catch (error) {
      console.error('Error adding interaction:', error);
      showSnackbar("Fehler beim Erstellen der Interaction", "error");
    }
  };

  const handleInteractionChange = (event) => {
    setNewInteraction({ ...newInteraction, [event.target.name]: event.target.value });
  };

  const handleTypeSelect = (type) => {
    setNewInteraction({ ...newInteraction, type });
  };

  const handleOpenEditInteraction = (interaction) => {
    setSelectedInteraction({
      ...interaction,
      due_date: interaction.due_date || null
    });
    setEditInteractionDialogOpen(true);
  };

  const handleEditInteractionChange = (event) => {
    setSelectedInteraction({ 
      ...selectedInteraction, 
      [event.target.name]: event.target.value 
    });
  };

  const handleEditTypeSelect = (type) => {
    setSelectedInteraction({ ...selectedInteraction, type });
  };

  const handleUpdateInteraction = async () => {
    try {
      const response = await axiosInstance.put(
        `/interactions/${selectedInteraction.id}/`,
        {
          type: selectedInteraction.type,
          title: selectedInteraction.title,
          description: selectedInteraction.description,
          assignee: selectedInteraction.assignee,
          due_date: selectedInteraction.due_date,
          related_type: relatedType,
          object_id: relatedId,
        }
      );

      setInteractions(interactions.map(i => 
        i.id === selectedInteraction.id ? response.data : i
      ));
      
      setEditInteractionDialogOpen(false);
      setSelectedInteraction(null);
      showSnackbar("Interaktion erfolgreich aktualisiert", "success");
    } catch (error) {
      console.error('Error updating interaction:', error);
      showSnackbar("Fehler beim Aktualisieren der Interaction", "error");
    }
  };

  const handleDeleteInteraction = async (interactionId) => {
    if (!window.confirm('Möchten Sie diese Interaction wirklich löschen?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/interactions/${interactionId}/`);
      setInteractions(interactions.filter(i => i.id !== interactionId));
      showSnackbar('Interaktion erfolgreich gelöscht', "success");
    } catch (error) {
      console.error('Error deleting interaction:', error);
      showSnackbar('Fehler beim Löschen der Interaktion', "error");
    }
  };

  const handleCompleteTask = async (interactionId) => {
    try {
      const response = await axiosInstance.patch(`/interactions/${interactionId}/`, {
        completed_at: new Date().toISOString()
      });
      setInteractions(interactions.map(i => i.id === interactionId ? { ...i, completed_at: response.data.completed_at } : i));
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const renderTypeSelector = (selectedType, onTypeSelect) => (
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
      <Tooltip title="Anruf">
        <IconButton 
          onClick={() => onTypeSelect('CALL')}
          sx={{ 
            backgroundColor: selectedType === 'CALL' ? getInteractionColor('CALL') : theme.palette.tokens.surface2,
            color: selectedType === 'CALL' ? 'white' : getInteractionColor('CALL'),
            '&:hover': { 
              backgroundColor: getInteractionColor('CALL'),
              color: 'white'
            }
          }}
        >
          <PhoneIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Email">
        <IconButton 
          onClick={() => onTypeSelect('EMAIL')}
          sx={{ 
            backgroundColor: selectedType === 'EMAIL' ? getInteractionColor('EMAIL') : theme.palette.tokens.surface2,
            color: selectedType === 'EMAIL' ? 'white' : getInteractionColor('EMAIL'),
            '&:hover': { 
              backgroundColor: getInteractionColor('EMAIL'),
              color: 'white'
            }
          }}
        >
          <EmailIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Aufgabe">
        <IconButton 
          onClick={() => onTypeSelect('TASK')}
          sx={{ 
            backgroundColor: selectedType === 'TASK' ? getInteractionColor('TASK') : theme.palette.tokens.surface2,
            color: selectedType === 'TASK' ? 'white' : getInteractionColor('TASK'),
            '&:hover': { 
              backgroundColor: getInteractionColor('TASK'),
              color: 'white'
            }
          }}
        >
          <TaskIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Meeting">
        <IconButton 
          onClick={() => onTypeSelect('MEETING')}
          sx={{ 
            backgroundColor: selectedType === 'MEETING' ? getInteractionColor('MEETING') : theme.palette.tokens.surface2,
            color: selectedType === 'MEETING' ? 'white' : getInteractionColor('MEETING'),
            '&:hover': { 
              backgroundColor: getInteractionColor('MEETING'),
              color: 'white'
            }
          }}
        >
          <MeetingRoomIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Notiz">
        <IconButton 
          onClick={() => onTypeSelect('NOTE')}
          sx={{ 
            backgroundColor: selectedType === 'NOTE' ? getInteractionColor('NOTE') : theme.palette.tokens.surface2,
            color: selectedType === 'NOTE' ? 'white' : getInteractionColor('NOTE'),
            '&:hover': { 
              backgroundColor: getInteractionColor('NOTE'),
              color: 'white'
            }
          }}
        >
          <NoteIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Interaktionen</Typography>
        <Button
          startIcon={showInteractionForm ? null : <AddIcon />}
          variant="contained"
          color={showInteractionForm ? "secondary" : "primary"}
          onClick={() => setShowInteractionForm(!showInteractionForm)}
        >
          {showInteractionForm ? "Abbrechen" : "Hinzufügen"}
        </Button>
      </Box>

      {interactions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Noch keine Interaktionen vorhanden.
        </Typography>
      )}

      {showInteractionForm ? (
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle1">Typ <span style={{ color: theme.palette.error.main }}> *</span></Typography>
          {renderTypeSelector(newInteraction.type, handleTypeSelect)}

          <TextField
            fullWidth
            margin="dense"
            label="Titel"
            name="title"
            value={newInteraction.title}
            onChange={handleInteractionChange}
            multiline
            required
            rows={1}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Beschreibung"
            name="description"
            value={newInteraction.description}
            onChange={handleInteractionChange}
            multiline
            required
            rows={3}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Zuweisen an</InputLabel>
            <Select
              name="assignee"
              value={newInteraction.assignee || ''}
              onChange={handleInteractionChange}
              label="Zuweisen an"
            >
              <MenuItem value={currentUser?.id}>Me</MenuItem>
              {users
                .filter(user => user.id !== currentUser?.id)
                .map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.email}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
            <DatePicker
              label="Fälligkeitsdatum"
              value={newInteraction.due_date ? dayjs(newInteraction.due_date) : null}
              onChange={(newValue) => {
                if (newValue && !dayjs(newValue).isValid()) {
                  setDateError('Ungültiges Datum');
                  return;
                }
  
                setDateError(null);
                if (newValue && dayjs(newValue).isValid()) {
                  setNewInteraction({
                    ...newInteraction,
                    due_date: newValue.toISOString()
                  });
                } else if (newValue === null) {
                  setNewInteraction({
                    ...newInteraction,
                    due_date: null
                  });
                }
              }}
              slotProps={{
                field: { clearable: true },
                textField: {
                  fullWidth: true,
                  error: !!dateError,
                  helperText: dateError || ''
                }
              }}
              sx={{ mb: 2 }}
            />
          </LocalizationProvider>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              onClick={handleAddInteraction} 
              variant="contained" 
              color="primary"
              fullWidth
            >
              Speichern
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ maxHeight: 'calc(60vh - 64px)', overflow: 'auto' }} >
          <Timeline
            sx={{
              px: 0,
              my: 0,
              [`& .MuiTimelineItem-root`]: {
                minHeight: 64,
                // TimelineOppositeContent entfernt → kein &:before nötig
              },
              [`& .MuiTimelineContent-root`]: {
                py: 1,
                px: 1,
              },
            }}
          >
            {interactions.map((interaction, index) => (
              <TimelineItem 
                key={interaction.id}
                // &:before ausblenden, da kein OppositeContent mehr verwendet wird
                sx={{
                  '&::before': { display: 'none' },
                  '&:hover': {
                    backgroundColor: theme.palette.tokens.hoverRow,
                    borderRadius: '8px',
                  },
                }}
              >
                {/* Linie + Dot */}
                <TimelineSeparator>
                  <Tooltip title={interaction.type_display}>
                    <TimelineDot
                      sx={{
                        bgcolor: getInteractionColor(interaction.type),
                        p: 0.5,
                        boxShadow: 'none',
                      }}
                    >
                      {React.cloneElement(getInteractionIcon(interaction.type), {
                        sx: { fontSize: 15 },
                      })}
                    </TimelineDot>
                  </Tooltip>

                  {index < interactions.length - 1 && (
                    <TimelineConnector sx={{ bgcolor: 'divider' }} />
                  )}
                </TimelineSeparator>

                {/* Content */}
                <TimelineContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>

                    {/* Hauptcontent-Zeile: Inhalt links, Datum rechts */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>

                      {/* Linke Seite: Titel + Beschreibung + Meta */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>

                        {/* Titel und Abgeschlossen-Status */}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, display: 'inline' }}
                          >
                            {interaction.title}
                          </Typography>

                          {interaction.completed_at && (
                            <Tooltip
                              title={`Abgeschlossen am ${dayjs(interaction.completed_at).format("DD.MM.YY")}`}
                            >
                              <CheckCircleIcon
                                sx={{
                                  fontSize: 16,
                                  color: 'success.main',
                                  ml: 0.5,
                                  verticalAlign: 'middle',
                                }}
                              />
                            </Tooltip>
                          )}
                        </Box>

                        {/* Beschreibung */}
                        {interaction.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.3, overflowWrap: 'anywhere' }}
                          >
                            {interaction.description}
                          </Typography>
                        )}

                        {/* Meta Infos */}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>

                          {interaction.assignee_name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {interaction.assignee_name}
                              </Typography>
                            </Box>
                          )}

                          {interaction.due_date && (
                            <Tooltip title="Fälligkeit">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {dayjs(interaction.due_date).format("DD.MM.YY")}
                                </Typography>
                              </Box>
                            </Tooltip>
                          )}

                        </Box>
                      </Box>

                      {/* Rechte Seite: Erstellungsdatum */}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap', pt: 0.2, flexShrink: 0 }}
                      >
                        {getRelativeDate(interaction.created_at)}
                      </Typography>

                    </Box>

                    {/* Hover-Aktionsleiste unterhalb */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.5,
                        height: 0,
                        overflow: 'hidden',
                        opacity: 0,
                        transition: 'opacity 0.2s, height 0.2s',
                        '.MuiTimelineItem-root:hover &': {
                          height: '28px',
                          opacity: 1,
                          overflow: 'visible',
                        },
                      }}
                    >
                      {!interaction.completed_at && (
                        <Tooltip title="Aufgabe abschließen">
                          <Button
                            size="small"
                            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
                            onClick={() => handleCompleteTask(interaction.id)}
                            sx={{
                              fontSize: 11,
                              py: 0.2,
                              px: 0.8,
                              color: theme.palette.statusColors.green,
                              borderColor: theme.palette.statusColors.green,
                              textTransform: 'none',
                              minWidth: 0,
                              '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.08) },
                            }}
                            variant="outlined"
                          >
                            Abschließen
                          </Button>
                        </Tooltip>
                      )}

                      <Tooltip title="Bearbeiten">
                        <IconButton onClick={() => handleOpenEditInteraction(interaction)} variant="action-edit" size="small">
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Löschen">
                        <IconButton onClick={() => handleDeleteInteraction(interaction.id)} variant="action-delete" size="small">
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                  </Box>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </Box>
      )}

      {/* Edit Interaction Dialog */}
      <Dialog 
        open={editInteractionDialogOpen} 
        onClose={() => setEditInteractionDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Interaktion bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Typ <span style={{ color: theme.palette.error.main }}> *</span></Typography>
            {renderTypeSelector(selectedInteraction?.type, handleEditTypeSelect)}

            <TextField
              fullWidth
              margin="dense"
              label="Titel"
              name="title"
              value={selectedInteraction?.title || ''}
              onChange={handleEditInteractionChange}
              required
              rows={1}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              margin="dense"
              label="Beschreibung"
              name="description"
              value={selectedInteraction?.description || ''}
              onChange={handleEditInteractionChange}
              multiline
              required
              rows={3}
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Zuweisen an</InputLabel>
              <Select
                name="assignee"
                value={selectedInteraction?.assignee || ''}
                onChange={handleEditInteractionChange}
                label="Zuweisen an"
              >
                <MenuItem value={currentUser?.id}>Mich</MenuItem>
                {users
                  .filter(user => user.id !== currentUser?.id)
                  .map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.first_name && user.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : user.email}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
              <DatePicker
                label="Fälligkeitdatum"
                value={selectedInteraction?.due_date ? dayjs(selectedInteraction.due_date) : null}
                onChange={(newValue) => {
                  if (newValue && !dayjs(newValue).isValid()) {
                    setDateError('Ungültiges Datum');
                    return;
                  }
    
                  setDateError(null);
                  if (newValue && dayjs(newValue).isValid()) {
                    setSelectedInteraction({
                      ...selectedInteraction,
                      due_date: newValue.toISOString()
                    });
                  } else if (newValue === null) {
                    setSelectedInteraction({
                      ...selectedInteraction,
                      due_date: null
                    });
                  }
                }}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    fullWidth: true,
                    error: !!dateError,
                    helperText: dateError || ''
                  }
                }}
              />
            </LocalizationProvider>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditInteractionDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleUpdateInteraction}
            variant="contained"
            color="primary"
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InteractionTimeline;